import { useEffect, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import {
  selectRideLiveSession,
  selectRideLiveSessionActions,
} from "../stores/ride-data.selectors"
import type { RideDataStoreHook } from "./use-fare-page-model"

export type RideFareCleanupPlatform = "appview" | "zalo"

/**
 * Clears stale live-session state when entering the fare screen.
 * **AppView vs Zalo rules differ intentionally** — do not merge branches without product/runtime sign-off.
 */
export function useRideFareStateCleanup(opts: {
  platform: RideFareCleanupPlatform
  useRideDataStore: RideDataStoreHook
}) {
  const { platform, useRideDataStore } = opts

  const {
    state: currentState,
    engagement,
    sessionId,
  } = useRideDataStore(useShallow(selectRideLiveSession))
  const { resetRideState } = useRideDataStore(
    useShallow(selectRideLiveSessionActions)
  )

  const hasCleanedRef = useRef(false)

  useEffect(() => {
    if (hasCleanedRef.current) return

    if (platform === "appview") {
      const needsCleaning = currentState || engagement || sessionId
      if (!needsCleaning) return
      resetRideState()
      hasCleanedRef.current = true
      return
    }

    const needsCleaning =
      (currentState && currentState !== "finding-driver") ||
      engagement ||
      sessionId

    if (!needsCleaning) return

    const cleanup = setTimeout(() => {
      resetRideState()
      hasCleanedRef.current = true
    }, 0)

    return () => clearTimeout(cleanup)
  }, [currentState, engagement, platform, resetRideState, sessionId])
}
